import { Controller } from "@hotwired/stimulus"
import { setProgress } from "../progress"

export default class extends Controller {
  static targets = ["image", "progress", "icon"]

  connect() {
    setProgress(this.progressTarget, 0)
  }

  showProgress() {
    setProgress(this.progressTarget, 0)
    this.progressTarget.classList.remove("hidden")
  }

  hideProgress() {
    this.progressTarget.classList.add("hidden")
  }

  activateIcon() {
    this.iconTarget.classList.add("swap-active")
    setTimeout(() => this.iconTarget.classList.remove("swap-active"), 2000)
  }

  unsupported(message, silent = false) {
    if (!silent) {
      alert(message)
    } else {
      console.warn(message)
    }
    this.hideProgress()
  }

  async hasClipboardSupport() {
    return navigator.clipboard && window.ClipboardItem
  }

  async checkClipboardPermission() {
    if (!navigator.permissions) return null
    try {
      const result = await navigator.permissions.query({ name: "clipboard-write" })
      return result.state
    } catch {
      return null
    }
  }

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  }

  async copy(event) {
    event.preventDefault()
    const imageUrl = this.imageTarget.dataset.imageClipboardCopyImage

    if (!await this.hasClipboardSupport()) {
      return this.unsupported("Clipboard image copy is not supported in this browser.")
    }

    const permission = await this.checkClipboardPermission()
    if (permission === "denied") {
      return this.unsupported("Clipboard permission denied. Please check your browser settings.")
    }

    if (this.isIOS()) {
      return this.unsupported("Copying images to clipboard is not supported on iOS browsers.", true)
    }

    this.showProgress()

    try {
      const { blob } = await this.fetchImageWithProgress(imageUrl)
      await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })])
      this.hideProgress()
      this.activateIcon()
    } catch (err) {
      this.unsupported("Failed to copy image: " + err.message)
    }
  }

  async fetchImageWithProgress(imageUrl) {
    let response
    try {
      response = await fetch(imageUrl)
    } catch (err) {
      this.unsupported("Failed to fetch image: " + err.message)
      throw err
    }


    if (!response.ok) {
      this.unsupported(`Failed to fetch image: HTTP ${response.status}`)
      throw new Error(`HTTP error ${response.status}`)
    }

    const contentLength = response.headers.get("content-length")
    if (!contentLength) {
      const blob = await response.blob()
      setProgress(this.progressTarget, 100)
      return { blob }
    }
    const total = parseInt(contentLength, 10)
    let loaded = 0
    const reader = response.body.getReader()
    const chunks = []

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      loaded += value.length
      setProgress(this.progressTarget, Math.round((loaded / total) * 100))
    }

    const blob = new Blob(chunks, { type: "image/png" })
    setProgress(this.progressTarget, 100)
    return { blob }
  }
}
